import { LABELS } from "./constants";

const TIME_RANGE_MINUTES: Record<string, number> = {
  last_5_minutes: 5,
  last_10_minutes: 10,
  last_15_minutes: 15,
  last_30_minutes: 30,
  last_1_hour: 60
};

const TIME_RANGE_DAYS: Record<string, number> = {
  last_24_hours: 1,
  last_7_days: 7,
  last_30_days: 30,
  last_90_days: 90,
};

export const timeRangeToDates = (timeRange?: string): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date();
  if (TIME_RANGE_MINUTES[timeRange ?? '']) {
    start.setMinutes(end.getMinutes() - (TIME_RANGE_MINUTES[timeRange ?? ''] ?? 30));
  } else {
    start.setDate(end.getDate() - (TIME_RANGE_DAYS[timeRange ?? ''] ?? 30));
  }
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString()
  };
};

export type TimeRangeDateParams = {
  startDate: string;
  endDate: string;
  isOlderThan90Days?: boolean;
};

export const olderThan90DaysToDateParams = (): TimeRangeDateParams => {
  const end = new Date();
  end.setDate(end.getDate() - 90);

  return {
    startDate: '',
    endDate: end.toISOString(),
    isOlderThan90Days: true
  };
};

export const buildDatePayload = (dateParams: TimeRangeDateParams) => {
  const { startDate, endDate, isOlderThan90Days } = dateParams;

  if (isOlderThan90Days) {
    return {
      endDate
    };
  }

  return {
    startDate,
    endDate
  };
};

export const transformTrendData = (
  rawData: any[],
  bucketKey: string,
  type?: string
) => {
  if (!rawData || rawData.length === 0) {
    return {
      series: [],
      stats: {
        totalAlerts: 0,
        peakDay: "-",
        avgPerDay: 0,
      },
    };
  }

  const label = LABELS?.[type ?? ""] || "alerts";

  // -------------------------------------------------------
  // 1. Sort by date
  // -------------------------------------------------------
  const sortedData = [...rawData].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // -------------------------------------------------------
  // 2. Collect keys
  // -------------------------------------------------------
  const keySet = new Set<string>();
  sortedData.forEach((day) => {
    Object.keys(day[bucketKey] ?? {}).forEach((k) => keySet.add(k));
  });
  const keys = Array.from(keySet);

  // -------------------------------------------------------
  // 3. Series
  // -------------------------------------------------------
  const series = keys.map((key) => ({
    name: key,
    data: sortedData.map((day) => ({
      date: day.date,
      count: day[bucketKey]?.[key] ?? 0,
    })),
  }));

  // -------------------------------------------------------
  // 4. Daily totals
  // -------------------------------------------------------
  const dailyTotals = sortedData.map((day) => {
    const total = Object.values(day[bucketKey] ?? {}).reduce(
      (s: number, v: any) => s + v,
      0
    ) as number;

    return {
      date: day.date,
      total,
    };
  });

  // -------------------------------------------------------
  // 5. Stats basics
  // -------------------------------------------------------
  // INFO: summing per-day unique counts, which will NEVER equal global unique count.
  // here we are doing totalAlerts = SUM (daily unique counts)
  // but elasticsearch is doing total_unique = CARDINALITY over entire dataset
  const totalAlerts = dailyTotals.reduce((s, d) => s + d.total, 0);

  const peakDayEntry = dailyTotals.reduce(
    (max, d) => (d.total > max.total ? d : max),
    dailyTotals[0]
  );

  const peakDay = peakDayEntry?.date ?? "-";
  const peakDaySubtitle = peakDayEntry?.total
    ? `${peakDayEntry.total.toLocaleString("en-US")} ${label}`
    : undefined;

  const avgPerDay =
    dailyTotals.length > 0
      ? Number(
          (
            totalAlerts /
            Math.max(
              dailyTotals.filter((d) => d.total > 0).length,
              1
            )
          ).toFixed(1)
        )
      : 0;

  // -------------------------------------------------------
  // 6. Top key
  // -------------------------------------------------------
  const keyCounts: Record<string, number> = {};

  keys.forEach((k) => (keyCounts[k] = 0));

  sortedData.forEach((day) => {
    keys.forEach((k) => {
      keyCounts[k] += day[bucketKey]?.[k] ?? 0;
    });
  });

  const topKey =
    Object.entries(keyCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "-";

  // -------------------------------------------------------
  // 7. TREND (FIXED & BOUNDED)
  // -------------------------------------------------------

  const WINDOW = Math.max(3, Math.min(7, Math.floor(dailyTotals.length / 2)));

  const recent = dailyTotals.slice(-WINDOW);
  const previous = dailyTotals.slice(-2 * WINDOW, -WINDOW);

  const sum = (arr: { total: number }[]) =>
    arr.reduce((s, d) => s + d.total, 0);

  const recentSum = sum(recent);
  const previousSum = sum(previous);

  let avgTrend: "up" | "down" | "neutral" = "neutral";

  // -------------------------------------------------------
  // 8. SAFE bounded percentage formula
  // -------------------------------------------------------
  let pctChange = 0;

  if (recent.length > 0 && previous.length > 0) {
    const denominator = recentSum + previousSum;

    if (denominator > 0) {
      const changeRatio = (recentSum - previousSum) / denominator;

      pctChange = Math.round(Math.abs(changeRatio * 100));

      if (changeRatio > 0) avgTrend = "up";
      else if (changeRatio < 0) avgTrend = "down";
    }
  }

  const avgSubtitle =
    pctChange > 0
      ? avgTrend === "up"
        ? `${pctChange}% increase`
        : `${pctChange}% decrease`
      : undefined;

  // -------------------------------------------------------
  // 9. Return
  // -------------------------------------------------------
  return {
    series,
    stats: {
      totalAlerts,
      peakDay,
      peakDaySubtitle,
      avgPerDay,
      avgTrend,
      avgSubtitle,
      topSector: topKey,
      topProgram: topKey,
    },
  };
};

export const buildFilterOptions = (alertsData: any[]) => {
  const unique = (arr: any[]) => Array.from(new Set(arr.filter(Boolean))).sort();
  return {
    severity: unique(alertsData.map((a) => a.mappedSeverity)),
    status: unique(alertsData.map((a) => a.state)),
    sector: unique(alertsData.map((a) => a.sector)),
    programs: unique(alertsData.map((a) => a.program)),
    source: unique(alertsData.map((a) => a.source)),
    assignmentGroup: unique(alertsData.map((a) => a.assignment_group)),
    domains: unique(alertsData.map((a) => a.domain)),
    businessUnits: unique(alertsData.map((a) => a.businessUnit)),
  };
};
